import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { contentDate, reviewedIso } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import Link from 'next/link'
import { RequestProvider } from '../../../context/RequestContext'
import { AirconRepair } from '../../../components/index/service-category/aircon-service/AirconRepair'
import { Services } from '../../../components/index/service/Services'
import { airconCostCalculation } from '../../../js/aircon-cost'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices } from '../../../service/wp-blogs-store'
import { BottomButtons } from '@/index/service-category/aircon-service/BottomButtons'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-10-23'

export async function getStaticProps() {
    let lat = 1.3521
    let lng = 103.8198

    const { month, year } = contentDate(CONTENT_REVIEWED);
    const modifiedDate = reviewedIso(CONTENT_REVIEWED);
    const airconBlogs = blogsMaster['hvac-services'].blogArray;
    const wpPosts = getWpHvacServices().length > 0 ?  getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services");
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ?  getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services");
    setWpNewHvacServices(wpNewPosts)

    const countryName = "Singapore"
    const allRequestCategorySettings = await getRequestJSON(countryName)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    const currencyCd = "SGD"

    let standardServicing = []
    let annualServicing = []
    let chemicalServicing = []
    let overhaulServicing = []    

    airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)

  return {
      props: {
        requestCategorySettings,
        modifiedDate,
        month,
        year,
        airconBlogs,
        standardServicing,
        countryName,
        currencyCd,
        wpNewPosts,
        wpPosts
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const router = useRouter()
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const airconBlogs = props.airconBlogs
    const requestCategorySettings = props.requestCategorySettings
    const standardServicing = props.standardServicing
    const currencyCd = props.currencyCd
    const requestType = 'Aircon Services'
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts
    const countryName = props.countryName

    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ isQuickBook, setIsQuickBook ] = useState(false)

  return (
    <>
      <Head>
        <title>Aircon Blower Replacement Cost | Installation & Repair</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Aircon Blower replacement can cost anywhere between $200 to $800 or more. This cost typically includes the price of the blower unit itself, which can vary based on the specific system requirements and the brand."/>
        <meta name="keywords" content="Aircon blower"/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-blower" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Aircon Blower Replacement Cost | Installation & Repair`}/>
        <meta property="og:description" content="Aircon Blower replacement can cost anywhere between $200 to $800 or more. This cost typically includes the price of the blower unit itself, which can vary based on the specific system requirements and the brand." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-18T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/air-conditioner-blower.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/air-conditioner-blower.jpg" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-blower"/>
        <meta name="twitter:title" content={`Aircon Blower Replacement Cost | Installation & Repair`}/> 
        <meta name="twitter:description" content="Aircon Blower replacement can cost anywhere between $200 to $800 or more. This cost typically includes the price of the blower unit itself, which can vary based on the specific system requirements and the brand."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-blower"/>
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@context":"https://schema.org/","@type":"BlogPosting","mainEntityOfPage":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-blower"},"headline":"Aircon Blower Replacement Cost | Installation & Repair","description":"Aircon Blower replacement can cost anywhere between $200 to $800 or more. This cost typically includes the price of the blower unit itself, which can vary based on the specific system requirements and the brand.","image":["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/air-conditioner-blower.jpg"],"author":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"publisher":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","logo":{"@type":"ImageObject","url":"https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"}},"datePublished":"2025-01-18T07:30:46+00:00","dateModified":"${modifiedDate}"},{"@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"}},{"@type":"ListItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore","name":"Aircon Servicing Singapore","url":"https://www.stringssg.com/sg/aircon-servicing-singapore"}},{"@type":"ListItem","position":3,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-blower","name":"Aircon Blower","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-blower"}}]}]}`
          }}
        />
        </Head>
        <div>
            <RequestProvider>
                <section id="aircon-blower-repair-replacement" className='relative'>
                    <AirconRepair 
                        setRequestForm={setRequestForm} 
                        setIsQuickBook={setIsQuickBook} 
                        title={"Aircon Blower Replacement Cost | Installation & Repair"} 
                        problem={"Replace/Repair Aircon Blower for [brand name]"}
                        heroImage="blog/living/air-conditioner-blower.jpg"
                    />
                </section>
                {!requestForm &&
                    <BottomButtons countryName={countryName}/>
                }
                {requestForm && 
                    <Services
                        requestType={requestType}
                        requestCategorySettings={requestCategorySettings}
                        setRequestForm={setRequestForm}
                        isQuickBook={isQuickBook}
                        isCustomBook={false}
                        asSheet
                    />
                }
            </RequestProvider>
            <div className='max-w-5xl mx-auto py-10 px-2 leading-8'>
                <div className='py-2 space-y-5'>
                    <div className='space-y-2'>
                        <h2 className='font-bold text-xl md:text-2xl uppercase'>What is an aircon blower?</h2>
                        <p className=''>
                            An aircon blower, also known as an air conditioner blower or simply a blower, is a component of an air conditioning system that is responsible for 
                            circulating air throughout the system. It is typically a motor-driven fan that draws in air from the surrounding environment and forces it through the 
                            air conditioning unit, where it is cooled or heated before being distributed into the desired space.
                        </p>
                        <p>
                            The blower plays a crucial role in the overall functioning of an air conditioning system. It helps to move air across the evaporator coil, where 
                            heat is absorbed from the indoor air, or across the condenser coil, where heat is released to the outside environment. By circulating air, the blower 
                            facilitates the transfer of heat, helping to cool or warm the air within a room or building.
                        </p>
                    </div>
                    <div className='space-y-2'>
                        <h2 className='font-bold text-2xl'>How does the Aircon blower work?</h2>
                        <p>
                            The aircon blower works by drawing in air from the surrounding environment and moving it through the air conditioning system. Here&apos;s a simplified explanation of how it operates:
                        </p>
                        <div className='space-y-2'>
                            <h3 className='font-bold'>1. Air Intake</h3>
                            <p>
                            The blower pulls in air from the room or space through a grille or vent located on the unit&apos;s front or side. This air is usually at room temperature.
                            </p>
                        </div>
                        <div className='space-y-2'>
                            <h3 className='font-bold'>2. Filtered Air</h3>
                            <p>
                            Before entering the blower, the air may pass through a filter to remove dust, allergens, and other particles, improving air quality.
                            </p>
                        </div>
                        <div className='space-y-2'>
                            <h3 className='font-bold'>3. Blower Motor</h3>
                            <p>
                            The blower is driven by an electric motor, usually located inside the indoor unit or air handler. When the motor is powered on, it starts rotating the 
                            fan blades.
                            </p>
                        </div>     
                        <div className='space-y-2'>
                            <h3 className='font-bold'>4. Airflow Generation</h3>
                            <p>
                            As the fan blades spin, they create airflow by pushing the air forward. The rotation of the blades creates a low-pressure area behind them, which draws 
                            air from the intake and propels it out through the outlet.
                            </p>
                        </div>
                        <div className='space-y-2'>
                            <h3 className='font-bold'>5. Heat Exchange</h3>
                            <p>
                            The aircon system has two sets of coils: the evaporator coil and the condenser coil. The blower helps in the heat exchange process by blowing air across 
                            these coils.
                            <br/>
                            a. Cooling Mode: In cooling mode, the blower pulls warm air from the room over the evaporator coil. The evaporator coil contains a cold refrigerant that 
                            absorbs heat from the air, cooling it down. The blower then blows the cooled air back into the room.
                            <br/>
                            b. Heating Mode: In heating mode (for systems that provide both cooling and heating), the blower draws in cooler air from the room and blows it over the
                             condenser coil. The condenser coil contains a hot refrigerant that releases heat into the air, warming it up. The blower then circulates the heated air 
                             into the room.
                            </p>
                        </div>   
                        <div className='space-y-2'>
                            <h3 className='font-bold'>6. Air Distribution</h3>
                            <p>
                            Once the air has been cooled or heated, the blower distributes it through the air conditioning system&apos;s ductwork or vents, delivering conditioned air to 
                            the desired space.
                            </p>
                        </div>
                        <p>
                        The speed of the blower can often be adjusted to control the airflow and achieve the desired temperature and comfort level. It is controlled by the air 
                        conditioning system&apos;s thermostat or through manual adjustments.
                        </p>
                    </div> 
                    <div className='space-y-2'>
                        <h3 className='font-bold text-2xl'>Where is the blower located?</h3>
                        <p>
                            The blower is usually located in the indoor unit of a split air conditioning system or within the air handler of a central air conditioning system. 
                            It is connected to a motor that drives the fan blades, enabling them to spin and create airflow. The speed of the blower can often be adjusted to 
                            control the volume of air being circulated, allowing for temperature and airflow regulation.
                        </p>
                    </div>                     
                    <div className='relative h-96'>
                        <Image className='rounded-lg'
                            src={s3Static + "blog/living/cool-air-from-aircon.jpg"}
                            fill
                            style={{objectFit: 'cover'}}
                            placeholder='blur'
                            blurDataURL={s3Static + "placeholder.png"}
                            alt='aircon blower blowing cold air'
                        />          
                    </div>
                    <div className='py-2 space-y-2'>
                        <h2 className='text-xl font-bold uppercase'>When to replace or repair an Aircon Blower?</h2>
                        <div className='space-y-2'>
                            <p className=''>
                            The decision to replace or repair an aircon blower depends on several factors, including the severity of the issue, the age of the blower, the cost of 
                            repair versus replacement, and the availability of replacement parts. Here are some considerations:
                            </p>
                            <div className='space-y-2'>
                                <h3 className='font-bold'>1. Severity of the Issue</h3>
                                <p>
                                If the blower has minor issues that can be easily repaired, such as a loose connection or a worn-out belt, repair may be the most appropriate option. 
                                However, if the blower motor is completely burnt out or the fan blades are severely damaged, replacement might be necessary.
                                </p>
                            </div>
                            <div className='space-y-2'>
                                <h3 className='font-bold'>2. Age of the Blower</h3>
                                <p>
                                Consider the age of the blower when deciding whether to repair or replace it. If the blower is relatively new and still within its expected lifespan, 
                                repairing it might be a cost-effective choice. However, if the blower is old and nearing the end of its lifespan, replacing it could be a better 
                                long-term solution, as other components may also start failing soon.
                                </p>
                            </div>
                            <div className='space-y-2'>
                                <h3 className='font-bold'>3. Cost of Repair versus Replacement</h3>
                                <p>
                                Compare the cost of repairing the blower with the cost of a new blower unit. In some cases, especially if the repair is minor, repairing the blower 
                                can be more economical. However, if the repair costs are high or if multiple components need replacement, it might be more cost-effective to invest 
                                in a new blower.
                                </p>
                            </div>     
                            <div className='space-y-2'>
                                <h3 className='font-bold'>4. Availability of Replacement Parts</h3>
                                <p>
                                Consider the availability of replacement parts for the specific blower model. If the blower is older or less common, finding the necessary parts for 
                                repair may be challenging or expensive. In such cases, replacing the blower with a new unit could be a more viable option.
                                </p>
                            </div>
                            <div className='space-y-2'>
                                <h3 className='font-bold'>5. Energy Efficiency and Performance</h3>
                                <p>
                                Upgrading to a new blower may offer improved energy efficiency and better performance compared to repairing an older blower. Newer models often 
                                incorporate advancements in technology and design that can enhance the overall efficiency and functionality of the air conditioning system.
                                </p>
                            </div> 
                            <div className='space-y-2'>
                                <h3 className='font-bold'>6. Professional Assessment</h3>
                                <p>
                                It is recommended to consult a qualified HVAC technician or air conditioning specialist to inspect the blower and provide expert advice. They can 
                                assess the extent of the damage, evaluate the repair options, and offer recommendations based on their expertise.
                                </p>
                            </div>                   
                        </div>
                    </div>
                    <div className='py-2 space-y-2'>
                        <h2 className='text-xl font-bold uppercase'>Aircon Blower Replacement Cost</h2>
                        <div className='space-y-5'>
                            <p>
                                The cost of replacing an aircon blower can vary depending on several factors, including the brand and model of the air conditioning system, the 
                                type of blower required, and the labor charges in your location. Additionally, the overall cost may also depend on whether other components, 
                                such as the blower motor or fan blades, need to be replaced along with the blower.
                            </p>
                            <p>
                                As an estimate, the cost of a replacement aircon blower can range from $200 to $800 or more. This cost typically includes the price of the 
                                blower unit itself, which can vary based on the specific system requirements and the brand. Additionally, you may need to factor in the cost of 
                                any associated parts, such as the blower motor or fan blades, if they are not included with the blower unit.
                            </p>
                        </div>
                    </div>             
                </div> 
            </div>
            <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/> 
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}